import type { QueryClient } from '@tanstack/react-query';

import { api } from './http';
import type { TaskDisposition, TaskKind } from './todoist';
import type { BoardScope } from '../pages/tasks/types';

export interface TaskAssignmentError {
  code: 'TASK_ASSIGNEE_CANNOT_ACCESS_MATTER' | 'CASE_MANAGEMENT_PARALEGAL_REQUIRED';
  message: string;
}

/** Extract only stable, user-safe assignment failures. Server detail may be
 * diagnostic-only, so the UI never renders it. */
export function taskAssignmentError(error: unknown): TaskAssignmentError | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const code = (detail as { code?: unknown }).code;
  if (code === 'TASK_ASSIGNEE_CANNOT_ACCESS_MATTER') {
    return { code, message: 'The selected person cannot receive tasks for this matter.' };
  }
  if (code === 'CASE_MANAGEMENT_PARALEGAL_REQUIRED') {
    return { code, message: 'Assign a case-management paralegal before moving this matter.' };
  }
  return null;
}

export interface BoardUserRef {
  id: number;
  name: string;
  // Task 7 (inactive-queue UI): mirrors the backend's User.active. Optional
  // so existing fixtures/tests that omit it (created before this field
  // existed) stay valid -- `=== false` is the only check that should ever
  // gate UI, never a bare truthiness check on this field.
  active?: boolean;
}

export interface BoardMatterRef {
  id: number;
  title: string;
}

export interface BoardTaskHistory {
  event_type: string;
  actor_name: string | null;
  created_at: string;
}

export interface BoardTaskItem {
  id: number;
  content: string | null;
  description: string | null;
  status: string;
  priority: number;
  due_date: string | null;
  task_kind: TaskKind;
  disposition: TaskDisposition | null;
  matter: BoardMatterRef | null;
  // A8 (Task Center Phase 2): matter_masked distinguishes "no matter"
  // (matter === null, matter_masked === false) from "matter masked"
  // (matter === null, matter_masked === true) — can_edit/can_complete are
  // server-computed mirrors of the existing PATCH/complete/reopen
  // authorization rule so the UI never renders a dead affordance.
  matter_masked: boolean;
  can_edit: boolean;
  can_reassign?: boolean;
  can_complete: boolean;
  can_attach: boolean;
  assigned_to: BoardUserRef | null;
  created_by: BoardUserRef | null;
  mirror_state: 'live' | 'detached' | 'off';
  todoist_task_id: string | null;
  source_system?: string | null;
  source_key?: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // Latest REASSIGNED event only (a later due-date change must not mask a
  // reassignment and drop the byline back to the creator).
  history: BoardTaskHistory | null;
  // Latest due-date change, carried separately from `history`.
  due_change: BoardTaskHistory | null;
  // Spec 2026-07-30 WP B: always present on the board (0 when none).
  attachment_count: number;
}

export interface BoardCounts {
  overdue: number;
  today: number;
  upcoming: number;
  no_date: number;
  completed_30d: number;
}

export interface TaskBoardResponse {
  user: BoardUserRef;
  tasks: BoardTaskItem[];
  counts: BoardCounts;
  requested_task_id: number | null;
  resolved_view: 'active' | 'completed';
}

export interface GetTaskBoardParams {
  user_id: number;
  view?: 'active' | 'completed';
  task_id?: number;
  scope?: BoardScope;
}

export const taskBoardKey = (params: GetTaskBoardParams) =>
  ['task-board', params.user_id, params.view ?? 'active', params.task_id ?? null, params.scope ?? 'assigned'] as const;

export const getTaskBoard = (params: GetTaskBoardParams) =>
  api.get<TaskBoardResponse>('/tasks/board', { params }).then((r) => r.data);

export interface TaskStaffingWarning {
  code: 'INTAKE_PARALEGAL_UNAVAILABLE' | 'CASE_PARALEGAL_UNAVAILABLE';
  message: string;
}

export interface EligibleAssigneesResponse {
  assignees: BoardUserRef[];
  staffing_warning: TaskStaffingWarning | null;
}

export const eligibleTaskAssigneesKey = (taskId: number) =>
  ['task-eligible-assignees', taskId] as const;

export const getEligibleTaskAssignees = (taskId: number) =>
  api.get<EligibleAssigneesResponse>(`/tasks/${taskId}/eligible-assignees`).then((r) => r.data);

export interface UnnoticedCollectorAddress {
  id: number;
  label: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  zip_code: string;
}

export interface UnnoticedCollectorHandoff {
  task_id: number;
  matter_id: number;
  creditor_id: number;
  creditor_name: string;
  addresses: UnnoticedCollectorAddress[];
  letter_kind: 'arn';
  mail_type: 'certified_return_receipt';
}

export type UnnoticedCollectorOutcome =
  | 'covered_by_existing_arn'
  | 'arn_not_required'
  | 'arn_required'
  | 'needs_attorney_review';

export interface UnnoticedCollectorResolutionResponse {
  task_id: number;
  matter_id: number;
  creditor_id: number;
  outcome: UnnoticedCollectorOutcome;
  state: 'completed' | 'pending_mail' | 'escalated';
  assigned_to: number | null;
  mailing_id: number | null;
}

export const unnoticedCollectorHandoffKey = (taskId: number) =>
  ['unnoticed-collector-handoff', taskId] as const;

export const getUnnoticedCollectorHandoff = (taskId: number) =>
  api.get<UnnoticedCollectorHandoff>(`/tasks/${taskId}/unnoticed-collector-handoff`).then((r) => r.data);

export const resolveUnnoticedCollectorTask = (
  taskId: number,
  outcome: UnnoticedCollectorOutcome,
) => api.post<UnnoticedCollectorResolutionResponse>(
  `/tasks/${taskId}/unnoticed-collector-resolution`,
  { outcome },
).then((r) => r.data);

/** The shared-cache-key invalidation contract (spec §5/§6): every
 * mutation fired from the Task Center page invalidates ITS OWN board
 * query prefix plus the two pre-existing prefixes the hub/Dashboard
 * already read from, so all three surfaces stay in sync exactly as
 * today — used by Tasks 6-8's complete/reopen/edit/create mutations. */
export const invalidateTaskCaches = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['task-board'] });
  queryClient.invalidateQueries({ queryKey: ['task-board-counts'] });
  queryClient.invalidateQueries({ queryKey: ['todoist-tasks'] });
  queryClient.invalidateQueries({ queryKey: ['my-work'] });
  queryClient.invalidateQueries({ queryKey: ['ai-hub', 'work-items'] });
  queryClient.invalidateQueries({ queryKey: ['briefing'] });
};

// PA11: mutations (complete/reopen/update/create/etc.) are imported
// directly from `./todoist` by consumers — this module is not a
// re-export layer for them.

export interface TaskCounts {
  overdue: number;
  today: number;
  upcoming: number;
  no_date: number;
}

/** P3-A3: self-only, tz-aware badge counts — see useAppBadge. */
export const taskCountsKey = (tz: string) => ['task-board-counts', tz] as const;

export const getTaskCounts = (tz: string) =>
  api.get<TaskCounts>('/tasks/board/counts', { params: { tz } }).then((r) => r.data);
